import * as React from 'react';
import * as _ from 'underscore';

import { defaultMemoize } from 'reselect';
import { Messages } from '../../../codegen/Localize';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ExtractTaskDefinition, RunFlowTaskDefinition, SubscriptionDefinition } from '../../../ts/models/ContentDefinitions';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { SiteTasksSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { addQuery, SpacesSiteUrls } from '../SpacesSiteUrls';
import { explorerSubspace } from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';

export interface SiteTasksSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'sitetasks'>;
  readonly contentCountsUpToDate: boolean;
}

export interface SiteTasksSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface SiteTasksSpaceOwnProps extends ReactDirectiveProps {
  readonly telemetry: Telemetry;
  readonly urls: SpacesSiteUrls;
}

export type SiteTasksSpaceProps = SiteTasksSpaceStateProps & SiteTasksSpaceOwnProps & SiteTasksSpaceDispatchProps;

export class SiteTasksSpace extends React.PureComponent<SiteTasksSpaceProps> {

  private memoizedGetSiteTasksSubspaces = defaultMemoize(getSiteTasksSubspaces);

  public componentDidUpdate(): void {
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
  }

  private static siteUrlBySubspace: Record<SiteTasksSubspaceName, (urls: SpacesSiteUrls) => string> = {
    extractRefreshes: (urls) => urls.tasksExtractRefreshesUrl(),
    runFlowTasks: (urls) => urls.tasksFlowsUrl(),
    subscriptions: (urls) =>  urls.tasksSubscriptionsUrl(),
    alerts: (urls) =>  urls.tasksDataAlertsUrl(),
  };

  public getSiteTasksUrl = (subspaceName?: SiteTasksSubspaceName) => {
    let url = this.props.urls.tasksUrl();
    if (subspaceName) {
      const urlFn = SiteTasksSpace.siteUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls) || url;
    }
    return url;
  }

  public render() {
    const subspaces = this.memoizedGetSiteTasksSubspaces(
      this.props.angularContext,
      this.props.telemetry,
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.getSiteTasksUrl}
        mastheadElement={undefined}  // For tasks, there isn't a masthead, but we are keeping the padding from masthead parent to stay consistent for the other admin spaces
        testIdPrefix={'sitetasks'}
        spaceItemName={undefined}  // For tasks, we don't need a separate browser title, just use the subspace
      />
    );
  }

  private fetchUpdatedCounts = (): void => {
    this.props.fetchUpdatedContentCounts();
  }
}

function getSiteTasksSubspaces(
  angularContext: AngularContext,
  telemetry: Telemetry,
  contentCounts: ISpaceInfoTypeItemCount<'sitetasks'>,
  contentCountsUpToDate: boolean
): ReadonlyArray<Subspace> {
  return _.compact([
    explorerSubspace({
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      angularContext,
      telemetry,
      contentCount: contentCounts && contentCounts['extractRefreshes'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
    }),
    explorerSubspace({
      name: 'runFlowTasks',
      tabLabel: Messages.flows(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      angularContext,
      telemetry,
      contentCount: contentCounts && contentCounts['runFlowTasks'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
    }),
    explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      angularContext,
      telemetry,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
    }),
    // explorerSubspace({
    //   name: 'alerts',
    //   tabLabel: Messages.dataAlerts(),
    //   supportedDisplayModes: DataAlertDefinition.supportedDisplayModes,
    //   supportFilterPanel: false,
    //   angularContext,
    //   telemetry,
    //   contentCount: contentCounts && contentCounts['alerts'],
    //   shouldRenderContentCount: true,
    //   contentCountUpToDate: contentCountsUpToDate,
    //   toolbarVisibility: 'enabled'
    // })
  ]);
}
